import { post } from '@/utils/request'
import { GOOGLE_AUTH_TYPES } from '@/common/constants/oauth'

/**
 * 第三方认证相关 API
 */

/**
 * @typedef {Object} VipInfo - VIP 信息
 * @property {boolean} is_vip - 是否 VIP
 * @property {number} vip_expire_at - VIP 过期时间戳
 * @property {boolean} is_new_vip - 是否新 VIP
 * @property {number} new_vip_expire_at - 新 VIP 过期时间戳
 * @property {string} new_vip_expire_format - 新 VIP 过期格式化时间
 * @property {boolean} is_svip - 是否 SVIP
 * @property {number} svip_expire_at - SVIP 过期时间戳
 * @property {string} svip_expire_format - SVIP 过期格式化时间
 */

/**
 * @typedef {Object} AbTestConfig - AB 测试配置
 * @property {string} message_style_ab - 消息样式实验组
 * @property {string} pay_style_ab - 支付样式实验组
 * @property {string} coin_center_ab - 金币中心实验组
 * @property {string} match_button_ab - 匹配按钮实验组
 * @property {string} moment_button_ab - 动态按钮实验组
 */

/**
 * @typedef {Object} UserAuthResponse - 用户认证响应
 * @property {string} id - 加密的用户 UID
 * @property {number} member_id - 用户 ID
 * @property {string} token - 用户认证 Token（融云/IM Token）
 * @property {number} age - 年龄
 * @property {string} nickname - 昵称
 * @property {number} sex - 性别（0-男，1-女）
 * @property {string} avatar - 头像 URL
 * @property {number} register_at - 注册时间戳
 * @property {number} role - 角色（0-普通用户，1-主播）
 * @property {string} action - 操作类型（"login"、"new_register"、"bind"）
 * @property {string} nation - 国家代码（如 "US"、"CN"）
 * @property {number} channel_id - 渠道 ID
 * @property {string} s_a - 安全模式状态（"on"/"off"）
 * @property {string} [anchor_type] - 主播类型（仅主播角色有值）
 * @property {boolean} visitor_account - 是否游客账号
 * @property {string[]} show_tab_list - 显示的 Tab 列表
 * @property {string} landing_page - 落地页标识
 * @property {string} recommended_tag - 推荐标签
 * @property {VipInfo} vip_info - VIP 信息对象
 * @property {number} coin - 金币/玫瑰数
 * @property {AbTestConfig} ab - AB 测试配置对象
 * @property {number} [quick_match_count] - 速配次数（仅在特定条件下返回）
 * @property {string} [zy_token] - 中台 IM Token（版本>=3.9.2）
 * @property {Object} [zy_im_config] - 中台 IM 配置（版本>=3.9.2）
 * @property {boolean} [zy_im_open] - IM 是否开启（版本>=3.9.2）
 */

/**
 * Google 登录认证
 * @param {Object} params - 认证参数
 * @param {string} params.redirect_uri - 重定向 URI (body 参数)
 * @param {string} [params.code] - 旧版谷歌登录的 code (query 参数)
 * @param {string} [params.action='login'] - 操作类型：login（登录）| auth_bind（绑定账号）| new_register（新注册）(query 参数)
 * @param {string} params.credential - 新版谷歌登录 credential(id_token) (body 参数)
 * @param {string} [params.nonce] - 用户登录 nonce (body 参数)
 * @returns {Promise<UserAuthResponse>} 返回用户完整信息
 */
export function googleAuth(data, config = {}) {
  const { code, action, credential, nonce, code_verifier, redirect_uri } = data
  let url = '/auth/v1/google_auth'
  let body = {
    redirect_uri,
    action,
  }
  // 根据 type 构建不同的请求参数
  if (data.type === GOOGLE_AUTH_TYPES.ID_TOKEN) {
    // One Tap 方式：google新登录方式，需要传id_token，后端校验有效性
    console.log('[Google Login] Using One Tap login, passing id_token')
    // 构建 body 参数
    body = {
      ...body,
      id_token: credential,
      nonce,
    }
  } else if (data.type === GOOGLE_AUTH_TYPES.AUTH_CODE) {
    // OAuth 授权码方式 + PKCE：传 code 和 code_verifier
    console.log('[Google Login] Using OAuth + PKCE login')
    // 构建 query 参数
    // const queryParams = new URLSearchParams()
    // queryParams.append('code', code)
    // code_verifier && queryParams.append('code_verifier', code_verifier)
    // queryParams.append('action', action)
    // url = `/auth/v1/google_auth?${queryParams.toString()}`
    body = {
      ...body,
      code,
      code_verifier,
    }
  }
  return post(url, body, {
    ...config,
    hideLoading: true,
    hideError: true,
  })
}

/**
 * Apple 登录认证
 * @param {Object} data - 认证数据
 * @param {string} data.code - Apple authorization code
 * @param {string} data.code_verifier - PKCE code_verifier
 * @param {string} [data.action='login'] - 操作类型：login（登录）| auth_bind（绑定账号）| new_register（新注册）
 * @returns {Promise<UserAuthResponse>} 返回用户完整信息
 */
export function appleAuth(data, config = {}) {
  return post('/auth/v1/apple_auth', {
      action: 'login',
      ...data,
    },
    {
      ...config,
      hideLoading: true,
      hideError: true,
    }
  )
}

/**
 * 刷新访问令牌
 * 使用 refresh token（存储在 HTTP-only cookie 中）刷新 access token
 * @returns {Promise<{access_token: string, user: Object}>} 新的 access token 和用户信息
 */
export function refreshToken(config = {}) {
  return post('/auth/v1/refresh_token', {}, {
    ...config,
    hideLoading: true,
    hideError: true,
  })
}

/**
 * 退出登录
 * 注销当前用户的登录状态，清除服务端 session 和 refresh token
 * @returns {Promise<void>} 无返回数据
 */
export function logout(data, config = {}) {
  return post('/auth/v1/logout', data, config)
}

/**
 * 注销账号
 * 请求注销当前登录的用户账号
 * @returns {Promise<void>} 无返回数据
 */
export function requestAccountCancellation(data, config = {}) {
  return post('/auth/v1/logouts/request', data, config)
}

/**
 * 手机号登录（仅测试环境）
 * @param {Object} data - 认证参数
 * @param {string} data.phone - 手机号（加密）
 * @param {string} data.captcha - 验证码
 * @param {string} data.action - 操作类型（login/register/bind）
 * @param {string} [data.auth_id] - 授权ID（可选）
 * @returns {Promise<UserAuthResponse>} 返回用户完整信息
 */
export function phoneAuth(data, config = {}) {
  return post('/auth/v1/phone_auth', data, config)
}
